/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';

import { Button, Typography2, useAccordionContext } from '@coursera/cds-core';
import { ChevronDownIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

interface Props extends React.PropsWithChildren {
  label: string;
}

const styles = {
  button: css`
    max-width: none;
    display: flex;
    justify-content: space-between;
    text-align: left;
    width: 100%;
    padding: var(--cds-spacing-200) var(--cds-spacing-400) var(--cds-spacing-200) var(--cds-spacing-200);

    :hover,
    :focus {
      background: transparent;
      text-decoration: none;
    }
  `,
  label: css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - var(--cds-spacing-400));
  `,
  labelText: css`
    min-width: 0;
    flex: 1;
  `,
  iconLabel: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--cds-spacing-50);
    white-space: nowrap;
    box-sizing: border-box;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-50);
    text-align: end;
    color: var(--cds-color-emphasis-primary-background-strong);
    width: fit-content;

    span {
      opacity: 0;
    }

    button:hover & {
      background: var(--cds-color-emphasis-primary-background-xweak);

      span {
        opacity: 1;
      }
    }

    button:hover & {
      text-decoration: underline;
    }
  `,
  icon: css`
    transition: transform 150ms ease-in-out;
    transform: rotate(0deg);
  `,
  iconRotated: css`
    transform: rotate(180deg);
  `,
};

const HeaderButton = ({ children, label }: Props) => {
  const context = useAccordionContext();
  const track = useTracker();
  const eventingProductData = useEventingProductData();

  const onClick = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const onToggleExpand = context?.onToggleExpanded;

      if (context?.expanded === false) {
        track('expand_accordion', { accordion: { name: 'course_details' }, ...eventingProductData });
      } else if (context?.expanded === true) {
        track('collapse_accordion', { accordion: { name: 'course_details' }, ...eventingProductData });
      }

      if (onToggleExpand) {
        onToggleExpand(event);
      }
    },
    [context, eventingProductData, track]
  );

  return (
    <Button
      ref={context?.headerRef}
      aria-expanded={context?.expanded || false}
      onClick={onClick}
      css={styles.button}
      variant="ghost"
      iconPosition="after"
    >
      <span css={styles.label}>
        <span css={styles.labelText}>{children}</span>
        <Typography2 variant="actionSecondary" component="span" css={styles.iconLabel} aria-hidden>
          <span>{label}</span>
          <ChevronDownIcon
            css={[styles.icon, context?.expanded ? styles.iconRotated : undefined]}
            size="medium"
            color="interactive"
          />
        </Typography2>
      </span>
    </Button>
  );
};

export default HeaderButton;
